import { diagnosticBlock, diagnosticOutcome, diagnosticTradeProvider, tradeDiagnostic, tradeProviderInfo, tradeSpan } from "./trade-diagnostics";
import { z } from "zod";
import { decodeEventLog, erc20Abi, type Address, type Hex } from "viem";
import { ELYSIUM_TRADE, sameTradeAddress } from "../config/trade";
import { DEX_TOKEN } from "../config/dex-token";
import { encodeNativeSwap, encodePoolApproval, nativeSwapReceipt, type SwapIdentity } from "../contracts/native-v3-trade";
import { currentChainId, waitForTransactionReceipt, type Eip1193Provider } from "./eip1193";
import { readRpc } from "./read-rpc";
import { observeTrade } from "./trade-readiness";
import { MERKLE_V4, assertMerkleV4Config, type MerkleV4Config } from "../config/merkle-v4";
import { encodeMerkleApproval, encodeMerkleSwap, merkleSwapReceipt, permit2Abi } from "../contracts/merkle-v4";
import { observeMerkleV4 } from "./merkle-v4-trade";

import { savedMerkleV4ConfigSchema } from "../api/merkle-v4-schema";

const address = z.string().regex(/^0x[0-9a-f]{40}$/i);
const uint = z.string().regex(/^(0|[1-9][0-9]*)$/).refine(value => BigInt(value) < 1n << 256n);
const hash = z.string().regex(/^0x[0-9a-f]{64}$/i);
const recordSchema = z.object({ id: z.string().min(1), account: address, recipient: address,
  chainId: z.literal(ELYSIUM_TRADE.chainId), sourceToken: address, pool: address, router: address,
  environment: z.string().min(1), side: z.enum(["BUY", "SELL"]), kind: z.enum(["APPROVAL", "SWAP"]),
  maximum: uint, minimum: uint, priceLimit: uint, deadline: uint,
  v4Config: savedMerkleV4ConfigSchema.optional(),
  poolId: hash.optional(), approvalStep: z.enum(["TOKEN", "PERMIT2"]).optional(), approvalExpiration: uint.optional(),
  hash: hash.nullable(), phase: z.enum(["PREPARING", "AWAITING_SIGNATURE", "PENDING", "UNCONFIRMED", "CONFIRMED", "REVERTED", "REJECTED", "FAILED", "REVIEW_REQUIRED"]),
  message: z.string(), blockHash: hash.optional(), blockNumber: z.string().regex(/^0x[0-9a-f]+$/i).optional(),
  actualInput: uint.optional(), actualOutput: uint.optional(), feeNative: uint.optional(), refund: uint.optional(),
}).strict();
export type SwapRecord = z.infer<typeof recordSchema>;
export type SwapRecordContext = Omit<SwapRecord, "id" | "hash" | "phase" | "message">;
export type SwapScope = Pick<SwapRecordContext, "account" | "chainId" | "environment" | "sourceToken" | "pool" | "router" | "poolId">;
export const SWAP_STORAGE_KEY = "merklewood.native-v3-transactions.v1";
export const swapUnresolved = (item: SwapRecord) => ["PREPARING", "AWAITING_SIGNATURE", "PENDING", "UNCONFIRMED", "REVIEW_REQUIRED"].includes(item.phase);
/** Active wallet work and approvals with a known hash reserve the form; hashless errors do not. */
export const swapBlocksSend = (item: SwapRecord) => (isV4(item) && swapUnresolved(item)) || item.phase === "PREPARING" || item.phase === "AWAITING_SIGNATURE" ||
  (item.kind === "APPROVAL" && Boolean(item.hash) && swapUnresolved(item));
export const sameSwapScope = (a: SwapScope, b: SwapScope) => sameTradeAddress(a.account, b.account) && a.chainId === b.chainId &&
  a.environment === b.environment && a.poolId === b.poolId && sameTradeAddress(a.sourceToken, b.sourceToken) && sameTradeAddress(a.pool, b.pool) && sameTradeAddress(a.router, b.router);
const isV4 = (item: SwapScope) => item.environment.includes(":merkle-v4:");
function savedV4Config(item: SwapRecordContext): MerkleV4Config {
  // Only old saved records use the historical identity; new sends carry their bootstrap snapshot.
  const config = item.v4Config ? { ...item.v4Config, deploymentBlock: BigInt(item.v4Config.deploymentBlock) } : MERKLE_V4;
  assertMerkleV4Config(config);
  return config;
}
const currentDeployment = (item: SwapRecordContext) => {
  if (isV4(item)) {
    const config = savedV4Config(item);
    return item.chainId === config.chainId && sameTradeAddress(item.sourceToken, config.token) &&
      sameTradeAddress(item.pool, config.poolManager) && sameTradeAddress(item.poolId, config.poolId) &&
      sameTradeAddress(item.router, config.router) && item.environment === `${config.environment}:merkle-v4:${config.poolId}`;
  }
  return item.chainId === ELYSIUM_TRADE.chainId && sameTradeAddress(item.sourceToken, ELYSIUM_TRADE.token) &&
    sameTradeAddress(item.pool, ELYSIUM_TRADE.pool) && sameTradeAddress(item.router, ELYSIUM_TRADE.router);
};
export function swapIdentity(item: SwapRecordContext): SwapIdentity {
  return { account: item.account as Address, recipient: item.recipient as Address, side: item.side,
    maximum: BigInt(item.maximum), minimum: BigInt(item.minimum), priceLimit: BigInt(item.priceLimit), deadline: BigInt(item.deadline) };
}
function expectedTransaction(item: SwapRecordContext) {
  if (!isV4(item)) return item.kind === "APPROVAL" ? encodePoolApproval(BigInt(item.maximum)) : encodeNativeSwap(swapIdentity(item));
  const config = savedV4Config(item);
  if (item.kind === "SWAP") return encodeMerkleSwap(swapIdentity(item), config);
  if (!item.approvalStep || !item.approvalExpiration || item.side !== "SELL") throw new Error("Missing V4 approval review.");
  return encodeMerkleApproval(BigInt(item.maximum), item.approvalStep, BigInt(item.approvalExpiration), config);
}

/** Session-scoped recovery, separate from NFT event identities. No signatures/calldata persisted. */
export class SwapTracker {
  private snapshot: { items: readonly SwapRecord[]; warning: string | null } = { items: [], warning: null };
  private listeners = new Set<() => void>();
  private pendingChecks = new Map<string, Promise<void>>();
  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem">,
    private readonly wait = waitForTransactionReceipt) {
    try {
      const raw = storage.getItem(SWAP_STORAGE_KEY);
      if (raw) this.snapshot.items = z.array(recordSchema).parse(JSON.parse(raw)).map(item => swapUnresolved(item) ?
        { ...item, phase: "UNCONFIRMED", message: item.hash ? "Saved hash; check confirmation without resending." :
          "No transaction hash was returned. The previous outcome is unknown." } : item);
    } catch { this.snapshot.warning = "Swap recovery storage is unreadable. Check wallet activity before any new send."; }
  }
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private update(id: string, change: Partial<SwapRecord>) {
    this.publish(this.snapshot.items.map(item => item.id === id ? { ...item, ...change } : item));
  }
  private publish(items: readonly SwapRecord[]) {
    let completed = 0;
    const retained = items.filter(item => swapUnresolved(item) || ++completed <= 10);
    let warning = this.snapshot.warning;
    try { this.storage.setItem(SWAP_STORAGE_KEY, JSON.stringify(retained.filter(item => item.phase !== "PREPARING"))); }
    catch { warning = "Swap recovery cannot be saved. Keep this tab open and check wallet activity before any new send."; }
    this.snapshot = { items: retained, warning };
    for (const listener of this.listeners) listener();
  }
  async execute(context: SwapRecordContext, provider: Eip1193Provider,
    prepare: () => Promise<{ to: Address; data: Hex; valueWei: bigint; gas?: bigint; assertCurrent(): void }>, guard: () => void,
    options: { onBroadcast?: () => void; readProvider?: Eip1193Provider } = {}): Promise<SwapRecord> {
    guard();
    if (!currentDeployment(context)) throw new Error("Swap deployment differs from the configured deployment.");
    if (this.snapshot.warning || this.snapshot.items.some(item => sameSwapScope(item, context) && swapBlocksSend(item)))
      throw new Error("Resolve the existing swap/approval or recovery storage warning before sending.");
    const record = recordSchema.parse({ ...context, id: crypto.randomUUID(), hash: null, phase: "PREPARING", message: "Checking reviewed operation…" });
    this.publish([record, ...this.snapshot.items]);
    let requested = false;
    let walletEnd: ReturnType<typeof tradeSpan> | undefined;
    try {
      const tx = await prepare(); guard(); tx.assertCurrent();
      const expected = expectedTransaction(context);
      if (!sameTradeAddress(tx.to, expected.to) || tx.data.toLowerCase() !== expected.data.toLowerCase() || tx.valueWei !== expected.valueWei)
        throw new Error("Swap/approval differs from the reviewed operation.");
      if (tx.gas !== undefined && tx.gas <= 0n) throw new Error("Invalid prepared gas limit.");
      if (await currentChainId(provider) !== ELYSIUM_TRADE.chainId) throw new Error(`WRONG_CHAIN:${ELYSIUM_TRADE.chainId}`);
      guard(); tx.assertCurrent();
      this.update(record.id, { phase: "AWAITING_SIGNATURE", message: context.kind === "APPROVAL" ?
        `Approve only this ${DEX_TOKEN.symbol} amount to ${isV4(context) ? context.approvalStep === "TOKEN" ? "Permit2" : "Router through Permit2" : "Pool"}.` : "Confirm the reviewed swap in your wallet." });
      if (this.snapshot.warning) throw new Error(this.snapshot.warning);
      requested = true;
      // This is the dapp request, NOT the user's Confirm click inside OKX.
      tradeDiagnostic(record.id, "wallet-request", { ...tradeProviderInfo(provider), method: "eth_sendTransaction" });
      walletEnd = tradeSpan(record.id, "wallet-send", { ...tradeProviderInfo(provider), method: "eth_sendTransaction", callCount: 1 });
      const result = await provider.request({ method: "eth_sendTransaction", params: [{ from: context.account, to: tx.to, data: tx.data,
        value: `0x${tx.valueWei.toString(16)}`, ...(tx.gas === undefined ? {} : { gas: `0x${tx.gas.toString(16)}` }) }] });
      walletEnd();
      // Do not apply the current wallet guard here: a late hash belongs to its original context.
      if (typeof result !== "string" || !/^0x[0-9a-f]{64}$/i.test(result)) throw new Error("Wallet send outcome has no valid hash.");
      tradeDiagnostic(record.id, "wallet-hash");
      this.update(record.id, { hash: result, phase: "PENDING", message: "Transaction sent. Waiting for on-chain confirmation." });
      // Notify presentation only after the hash is saved; receipt verification still owns settlement.
      options.onBroadcast?.();
      const verification = this.check(record.id, options.readProvider ?? provider);
      if (context.kind === "APPROVAL") await verification;
      else {
        verification.catch(() => this.update(record.id, { phase: "UNCONFIRMED",
          message: "Confirmation check failed. The saved hash will be checked again; no transaction was resent." }));
      }
    } catch (error) {
      walletEnd?.(diagnosticOutcome(error));
      const rejected = typeof error === "object" && error !== null && "code" in error && error.code === 4001;
      this.update(record.id, { phase: requested ? rejected ? "REJECTED" : "UNCONFIRMED" : "FAILED",
        message: requested && !rejected ? "No transaction hash was returned. The previous outcome is unknown." :
          error instanceof Error ? error.message : "Operation failed before broadcast." });
    }
    return this.snapshot.items.find(item => item.id === record.id)!;
  }
  check(id: string, provider: Eip1193Provider): Promise<void> {
    const existing = this.pendingChecks.get(id); if (existing) return existing;
    const pending = this.observe(id, provider).finally(() => { this.pendingChecks.delete(id); });
    this.pendingChecks.set(id, pending); return pending;
  }
  /** Resume known hashes after reload/RPC errors. Never infer success or resend. */
  watch(scope: SwapScope, provider: Eip1193Provider): () => void {
    let stopped = false, running = false, delay = 3000, cursor = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const candidates = () => this.snapshot.items.filter(item => sameSwapScope(item, scope) && item.hash &&
      (item.phase === "PENDING" || item.phase === "UNCONFIRMED"));
    const schedule = (ms: number) => {
      if (!stopped && !running && timer === undefined && document.visibilityState !== "hidden" && candidates().length)
        timer = setTimeout(() => { timer = undefined; poll().catch(() => schedule(30000)); }, ms);
    };
    const poll = async () => {
      if (stopped || running || document.visibilityState === "hidden") return;
      const items = candidates(); if (!items.length) return;
      cursor %= items.length;
      const batch = [...items.slice(cursor), ...items.slice(0, cursor)].slice(0, 3);
      cursor = (cursor + batch.length) % items.length;
      running = true;
      try { await Promise.all(batch.map(item => this.check(item.id, provider).catch(() => {
        this.update(item.id, { phase: "UNCONFIRMED", message: "Confirmation check failed. Retrying automatically; do not resend." });
      }))); } finally {
        running = false;
        const unresolved = this.snapshot.items.some(record => batch.some(item => item.id === record.id) && swapUnresolved(record));
        if (unresolved) { schedule(delay); delay = Math.min(delay * 2, 30000); }
        else { delay = 3000; schedule(0); }
      }
    };
    const unsubscribe = this.subscribe(() => schedule(3000));
    const visibility = () => {
      clearTimeout(timer); timer = undefined;
      if (document.visibilityState !== "hidden") { delay = 3000; schedule(0); }
    };
    document.addEventListener("visibilitychange", visibility);
    schedule(0);
    return () => {
      stopped = true; clearTimeout(timer); unsubscribe(); document.removeEventListener("visibilitychange", visibility);
    };
  }
  private async observe(id: string, provider: Eip1193Provider) {
    const item = this.snapshot.items.find(record => record.id === id);
    if (!item?.hash) throw new Error("No known hash. Inspect wallet activity first.");
    provider = diagnosticTradeProvider(provider, id);
    const verificationEnd = tradeSpan(id, "receipt-verification");
    try {
      if (!currentDeployment(item)) throw new Error("Saved transaction belongs to another deployment. Restore its original deployment to verify this hash.");
      const v4Config = isV4(item) ? savedV4Config(item) : null;
      const verifyDeployment = async (signal?: AbortSignal) => {
        const end = tradeSpan(id, "observeTrade");
        try {
          const head = await (v4Config ? observeMerkleV4(provider, () => {}, signal ? { signal } : {}, v4Config) : observeTrade(provider, () => {}, signal ? { signal } : {}));
          end("ok", { blockNumber: `0x${head.blockNumber.toString(16)}`, blockHash: head.blockHash });
        } catch (error) {
          end(signal?.aborted ? "cancelled" : diagnosticOutcome(error));
          throw error;
        }
      };
      const waitForReceipt = async (signal?: AbortSignal) => {
        const end = tradeSpan(id, "receipt-wait");
        try {
          const receipt = await this.wait(provider, item.hash as Hex, { expectedChainId: 99901, ...(signal ? { signal } : {}) });
          end("ok", diagnosticBlock(receipt));
          return receipt;
        } catch (error) {
          end(signal?.aborted ? "cancelled" : diagnosticOutcome(error));
          throw error;
        }
      };
      let receipt;
      if (item.kind === "APPROVAL") {
        // Receipt polling can run while deployment reads are in flight. Both
        // must pass before any receipt is interpreted or published.
        const abort = new AbortController();
        const deployment = verifyDeployment(abort.signal);
        const polling = waitForReceipt(abort.signal);
        try {
          [, receipt] = await Promise.all([deployment, polling]);
        } catch (error) {
          abort.abort(error);
          await Promise.allSettled([deployment, polling]);
          throw error;
        }
      } else {
        await verifyDeployment();
        receipt = await waitForReceipt();
      }
      if (!sameTradeAddress(receipt.transactionHash, item.hash) || !/^0x[0-9a-f]{64}$/i.test(receipt.blockHash) ||
        !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(receipt.blockNumber)) throw new Error("Invalid receipt identity; keep the hash and check again.");
      const block = await readRpc(provider, { method: "eth_getBlockByNumber", params: [receipt.blockNumber, false] }) as { hash?: string } | null;
      if (!block?.hash || !sameTradeAddress(block.hash, receipt.blockHash)) throw new Error("Receipt block changed; confirmation remains uncertain.");
      if (receipt.status === "0x0") { this.update(id, { phase: "REVERTED", message: "Transaction reverted on chain." }); return; }
      const expected = expectedTransaction(item);
      const raw = await readRpc(provider, { method: "eth_getTransactionByHash", params: [item.hash] }) as
        { from?: string; to?: string; input?: string; value?: string; hash?: string } | null;
      if (!raw?.hash || !sameTradeAddress(raw.hash, item.hash) || !sameTradeAddress(raw.from, item.account) ||
        !sameTradeAddress(raw.to, expected.to) || raw.input?.toLowerCase() !== expected.data.toLowerCase() ||
        !raw.value || !/^0x[0-9a-f]+$/i.test(raw.value) || BigInt(raw.value) !== expected.valueWei)
        throw new Error("Transaction identity could not be verified.");
      const result = item.kind === "SWAP" ? (v4Config ? merkleSwapReceipt(receipt.logs, swapIdentity(item), v4Config) : nativeSwapReceipt(receipt.logs, swapIdentity(item))) : null;
      const approval = item.kind === "APPROVAL" && receipt.logs.some(log => {
        if (v4Config && item.approvalStep === "PERMIT2") {
          if (!sameTradeAddress(log.address, v4Config!.permit2)) return false;
          try {
            const event = decodeEventLog({ abi: permit2Abi, data: log.data, topics: log.topics as [Hex, ...Hex[]] });
            return sameTradeAddress(event.args.owner, item.account) && sameTradeAddress(event.args.token, v4Config!.token) &&
              sameTradeAddress(event.args.spender, v4Config!.router) && event.args.amount === BigInt(item.maximum) &&
              BigInt(event.args.expiration) === BigInt(item.approvalExpiration!);
          } catch { return false; }
        }
        if (!sameTradeAddress(log.address, isV4(item) ? v4Config!.token : ELYSIUM_TRADE.token)) return false;
        try {
          const event = decodeEventLog({ abi: erc20Abi, data: log.data, topics: log.topics as [Hex, ...Hex[]] });
          return event.eventName === "Approval" && sameTradeAddress(event.args.owner, item.account) &&
            sameTradeAddress(event.args.spender, isV4(item) ? v4Config!.permit2 : ELYSIUM_TRADE.pool) && event.args.value === BigInt(item.maximum);
        } catch { return false; }
      });
      if (!(item.kind === "APPROVAL" ? approval : result)) {
        this.update(id, { phase: "REVIEW_REQUIRED", message: "Expected Pool swap or token approval event is missing. Keep the hash; do not resend." }); return;
      }
      // Recheck after all reads; hash alone is never confirmation and indexing is separate.
      const finalBlock = await readRpc(provider, { method: "eth_getBlockByNumber", params: [receipt.blockNumber, false] }) as { hash?: string } | null;
      if (await currentChainId(provider) !== ELYSIUM_TRADE.chainId || !sameTradeAddress(finalBlock?.hash, receipt.blockHash)) throw new Error("Receipt context changed.");
      verificationEnd("ok", diagnosticBlock(receipt));
      this.update(id, { phase: "CONFIRMED", blockHash: receipt.blockHash, blockNumber: receipt.blockNumber,
        message: item.kind === "APPROVAL" ? "Approval confirmed. Refresh allowance and quote, then review a separate swap." :
          "Pool swap verified. Authoritative balances, net-buy and vault are refreshing; indexing/finality remain separate.",
        ...(result ? { actualInput: result.amountIn.toString(), actualOutput: result.amountOut.toString(), feeNative: result.feeNative.toString(), refund: result.refund.toString() } : {}) });
    } catch (error) {
      verificationEnd(diagnosticOutcome(error));
      this.update(id, { phase: "UNCONFIRMED", message: error instanceof Error ? error.message : "Receipt unavailable; keep the hash and check again." });
    } finally { verificationEnd("error"); }
  }
}
